import { useState } from 'react';
import styled from 'styled-components';
import Button from '../../components/button/Button';
import DialogView from '../../components/dialog-view/DialogView';
import TextField from '../../components/text-field/TextField';
import Typography from '../../components/typography/Typography';

export type CreateRelationSide = 'preparation' | 'followUp';

interface Props {
  side: CreateRelationSide;
  mainTaskTitle: string;
  onClose: () => void;
  onConfirm: (minDays: number, maxDays: number) => void;
}

const DEFAULT_MIN_DAYS = '1';
const DEFAULT_MAX_DAYS = '1';

const isWholeDayCount = (value: string) => value !== '' && Number.isInteger(Number(value)) && Number(value) >= 0;

const CreateRelationDialog = (props: Props) => {
  const { side, mainTaskTitle, onClose, onConfirm } = props;
  const [minDays, setMinDays] = useState(DEFAULT_MIN_DAYS);
  const [maxDays, setMaxDays] = useState(DEFAULT_MAX_DAYS);

  const daysAreWholeNumbers = isWholeDayCount(minDays) && isWholeDayCount(maxDays);
  const rangeIsValid = daysAreWholeNumbers && Number(maxDays) >= Number(minDays);

  const relativeToMainTask = side === 'preparation' ? 'før' : 'efter';

  return (
    <DialogView open handleClose={onClose}>
      <Content data-testid="create-relation-dialog">
        <Typography variant="h5" fontWeight="bold">
          {side === 'preparation' ? 'Ny forberedelsesopgave' : 'Ny opfølgningsopgave'}
        </Typography>
        <Typography>
          {side === 'preparation'
            ? `Du er ved at oprette en forberedelsesopgave til '${mainTaskTitle}'.`
            : `Du er ved at oprette en opfølgningsopgave til '${mainTaskTitle}'.`}
        </Typography>
        <Row>
          <TextField
            id="create-relation-min-days"
            type="number"
            label={`Min. antal dage ${relativeToMainTask} hovedopgaven`}
            value={minDays}
            error={!rangeIsValid}
            onChange={(event) => setMinDays(event.target.value)}
          />
          <TextField
            id="create-relation-max-days"
            type="number"
            label={`Maks. antal dage ${relativeToMainTask} hovedopgaven`}
            value={maxDays}
            error={!rangeIsValid}
            onChange={(event) => setMaxDays(event.target.value)}
          />
        </Row>
        {!rangeIsValid && (
          <Typography variant="span" color="error">
            Angiv hele dage, hvor maks. ikke er mindre end min.
          </Typography>
        )}
        <Actions>
          <Button variant="secondary" onClick={onClose}>
            Annuller
          </Button>
          <Button
            disabled={!rangeIsValid}
            onClick={() => rangeIsValid && onConfirm(Number(minDays), Number(maxDays))}
            data-testid="create-relation-confirm"
          >
            Opret
          </Button>
        </Actions>
      </Content>
    </DialogView>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding: 24px;
  width: 420px;
  max-width: 90vw;
`;

const Row = styled.div`
  display: flex;
  column-gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 12px;
`;

export default CreateRelationDialog;
